import React, { useEffect, useState } from 'react';
import { Check, X, Sparkles } from 'lucide-react';
import { useNotification } from '../../notification/NotificationContext';
import {
  getPlans,
  createPaymentOrder,
  verifyPayment,
} from '../../services/mockInterviewService';
import { getStoredUser } from '../../utils/auth';
import { loadRazorpayScript, RAZORPAY_KEY } from '../../utils/razorpay';
import { useSubscription } from '../../context/SubscriptionContext';
import './PricingModal.css';

const inrPrice = (plan, cycle) =>
  cycle === 'Yearly'
    ? Number(plan?.priceYearlyInr ?? Math.round((plan?.priceYearlyPaise ?? 0) / 100))
    : Number(plan?.priceMonthlyInr ?? Math.round((plan?.priceMonthlyPaise ?? 0) / 100));

// ~20% discount → annualised yearly rate compared to 12 × monthly
const yearlyDiscount = (plan) => {
  const monthly = inrPrice(plan, 'Monthly');
  const yearly = inrPrice(plan, 'Yearly');
  if (monthly <= 0) return 0;
  const fullYear = monthly * 12;
  if (yearly >= fullYear) return 0;
  return Math.round(((fullYear - yearly) / fullYear) * 100);
};

const PricingModal = ({ open, onClose, onSubscribed }) => {
  const { showNotification } = useNotification();
  const { subscription, refresh: refreshSubscription } = useSubscription();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cycle, setCycle] = useState('Monthly'); // 'Monthly' | 'Yearly'
  const [busyKey, setBusyKey] = useState(null);  // planKey currently checking out

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getPlans()
      .then((d) => setPlans(Array.isArray(d) ? d.filter((p) => p.isActive !== false) : []))
      .catch((e) => showNotification(e.message || 'Failed to load plans', 'error'))
      .finally(() => setLoading(false));
    // Always refresh on open so the badge reflects the latest server state.
    refreshSubscription();
  }, [open, showNotification, refreshSubscription]);

  if (!open) return null;

  const currentPlanKey = (subscription?.planKey || 'Free').toString();

  const sorted = [...plans].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  // Highlight the middle paid card. Falls back to a plan with planKey === 'Plus'.
  const findHighlight = (list) => {
    const plus = list.findIndex((p) => /plus/i.test(p.planKey || ''));
    if (plus >= 0) return plus;
    const paid = list.filter((p) => inrPrice(p, 'Monthly') > 0);
    if (paid.length >= 2) return list.indexOf(paid[Math.floor(paid.length / 2)]);
    return -1;
  };
  const highlightIdx = findHighlight(sorted);

  const handleSubscribe = async (plan) => {
    if (inrPrice(plan, cycle) <= 0) {
      showNotification('You are on the free plan already.', 'info');
      onClose?.();
      return;
    }
    setBusyKey(plan.planKey);
    try {
      const order = await createPaymentOrder({
        planKey: plan.planKey,
        billingCycle: cycle,
      });

      // Load script first — if the network blocks it, fail gracefully instead
      // of letting `new window.Razorpay(...)` throw a ReferenceError.
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) {
        showNotification('Failed to load payment gateway. Check your internet connection.', 'error');
        setBusyKey(null);
        return;
      }

      // Prefill name/email from the stored auth user so the candidate doesn't
      // re-type it inside the Razorpay sheet.
      const u = getStoredUser() || {};

      const rzp = new window.Razorpay({
        key: RAZORPAY_KEY,
        amount: order.amountPaise,
        currency: order.currency || 'INR',
        order_id: order.orderId,
        name: 'SkillOn',
        description: `${order.planKey || plan.displayName} · ${order.billingCycle || cycle}`,
        prefill: {
          name: u.fullName || '',
          email: u.email || '',
        },
        theme: { color: '#6366f1' },
        handler: async (resp) => {
          try {
            await verifyPayment({
              razorpayOrderId: resp.razorpay_order_id,
              razorpayPaymentId: resp.razorpay_payment_id,
              razorpaySignature: resp.razorpay_signature,
              planKey: order.planKey || plan.planKey,
              billingCycle: order.billingCycle || cycle,
            });
            // Refresh global subscription so badges + navbar update immediately.
            await refreshSubscription();
            showNotification(`Subscribed to ${plan.displayName}!`, 'success');
            onSubscribed?.();
            onClose?.();
          } catch (err) {
            showNotification(
              err.message || 'Payment received but verification failed. Please contact support.',
              'error',
            );
          } finally {
            setBusyKey(null);
          }
        },
        modal: {
          // User dismissing the sheet is not an error — clear busy and stay quiet.
          ondismiss: () => setBusyKey(null),
          escape: true,
          backdropclose: false,
        },
      });

      rzp.on('payment.failed', (resp) => {
        showNotification(resp?.error?.description || 'Payment failed', 'error');
        setBusyKey(null);
      });

      rzp.open();
    } catch (e) {
      showNotification(e.message || 'Could not start checkout', 'error');
      setBusyKey(null);
    }
  };

  return (
    <div className="pm-backdrop" onClick={onClose}>
      <div className="pm-shell" onClick={(e) => e.stopPropagation()}>
        <button className="pm-close" onClick={onClose} aria-label="Close">
          <X size={20} />
        </button>

        <div className="pm-head">
          <p className="pm-kicker">Pricing</p>
          <h2 className="pm-title">Choose the plan that fits you</h2>
          <p className="pm-sub">Upgrade anytime. Cancel anytime.</p>

          <div className="pm-toggle" role="tablist" aria-label="Billing cycle">
            <button
              className={`pm-toggle-btn ${cycle === 'Monthly' ? 'pm-toggle-active' : ''}`}
              onClick={() => setCycle('Monthly')}
              role="tab"
              aria-selected={cycle === 'Monthly'}
            >
              Monthly
            </button>
            <button
              className={`pm-toggle-btn ${cycle === 'Yearly' ? 'pm-toggle-active' : ''}`}
              onClick={() => setCycle('Yearly')}
              role="tab"
              aria-selected={cycle === 'Yearly'}
            >
              Yearly <span className="pm-save-badge">Save up to 20%</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="pm-loading"><div className="mi-spin" /></div>
        ) : sorted.length === 0 ? (
          <div className="pm-empty">No plans available right now.</div>
        ) : (
          <div className="pm-grid">
            {sorted.map((plan, idx) => {
              const price = inrPrice(plan, cycle);
              const isFree = price <= 0;
              const isHighlight = idx === highlightIdx;
              const discount = cycle === 'Yearly' ? yearlyDiscount(plan) : 0;
              const features = Array.isArray(plan.features) ? plan.features : [];
              const busy = busyKey === plan.planKey;
              const isCurrent = (plan.planKey || '').toLowerCase() === currentPlanKey.toLowerCase();
              return (
                <div
                  key={plan.id || plan.planKey}
                  className={`pm-card ${isHighlight ? 'pm-card-popular' : ''} ${isCurrent ? 'pm-card-current' : ''}`}
                >
                  {isCurrent && !isHighlight && (
                    <div className="pm-current-flag">
                      <Check size={12} /> Current Plan
                    </div>
                  )}
                  {isHighlight && (
                    <div className="pm-popular-flag">
                      <Sparkles size={12} /> Most Popular
                    </div>
                  )}
                  <div className="pm-card-name">{plan.displayName}</div>
                  <div className="pm-card-price">
                    {isFree ? (
                      <span className="pm-price-free">Free</span>
                    ) : (
                      <>
                        <span className="pm-price-amount">
                          ₹{cycle === 'Yearly' ? Math.round(price / 12) : price}
                        </span>
                        <span className="pm-price-suffix">/mo</span>
                      </>
                    )}
                  </div>
                  {!isFree && cycle === 'Yearly' && (
                    <div className="pm-price-note">
                      Billed ₹{price}/year{discount > 0 ? ` · save ${discount}%` : ''}
                    </div>
                  )}
                  {!isFree && cycle === 'Monthly' && (
                    <div className="pm-price-note">Billed monthly</div>
                  )}
                  {plan.minutesAllowed > 0 && (
                    <div className="pm-minutes">
                      {plan.minutesAllowed} interview minutes / month
                    </div>
                  )}
                  <ul className="pm-features">
                    {features.map((f, i) => (
                      <li key={i}>
                        <Check size={14} className="pm-check" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <div className="pm-current-cta" aria-label="Current plan">
                      <Check size={16} /> Current Plan
                    </div>
                  ) : (
                    <button
                      className={`pm-cta ${isHighlight ? 'pm-cta-primary' : ''}`}
                      onClick={() => (isFree ? onClose?.() : handleSubscribe(plan))}
                      disabled={busy}
                    >
                      {busy ? 'Opening checkout…' : isFree ? 'Get Started' : 'Subscribe Now'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="pm-footnote">
          Payments are processed securely by Razorpay. By subscribing you agree to our terms.
        </p>
      </div>
    </div>
  );
};

export default PricingModal;
